import { CreateTask, Timestamp } from '@griply/types';
import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { CreateMultipleTasksDialogContext } from './create-multiple-tasks-dialog-context';

const loadCreateMultipleTasksDialog = () =>
  import('shared/dialogs/create-multiple-tasks-dialog');
const CreateMultipleTasksDialog = React.lazy(async () => ({
  default: (await loadCreateMultipleTasksDialog()).CreateMultipleTasksDialog,
}));

export type CreateMultipleTasksDialogProviderProps = {
  children: React.ReactNode;
};

export const CreateMultipleTasksDialogProvider: React.FC<
  CreateMultipleTasksDialogProviderProps
> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [taskNames, setTaskNames] = useState<string[]>([]);
  const [handleSubmit, setHandleSubmit] = useState<
    ((tasks: CreateTask[]) => Promise<void> | void) | undefined
  >();
  const [handleCancel, setHandleCancel] = useState<(() => void) | undefined>();
  const [date, setDate] = useState<Timestamp>();
  const [context, setContext] = useState<CreateTask>();

  const handleOpen = useCallback(
    (
      newTaskNames: string[],
      newHandleSubmit: (tasks: CreateTask[]) => Promise<void> | void,
      options?: {
        date?: Timestamp;
        context?: CreateTask;
        onCancel?: () => void;
      },
    ) => {
      setTaskNames(newTaskNames);
      setHandleSubmit(() => newHandleSubmit);
      setHandleCancel(() => options?.onCancel);
      setDate(options?.date);
      setContext(options?.context);
      setOpen(true);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setTaskNames([]);
    setHandleSubmit(undefined);
    setHandleCancel(undefined);
    setDate(undefined);
    setContext(undefined);
  }, []);

  const handleDialogCancel = useCallback(() => {
    handleCancel?.();
    handleClose();
  }, [handleCancel, handleClose]);

  const handleDialogSubmit = useCallback(
    async (submittedTasks: CreateTask[]) => {
      if (handleSubmit) {
        await handleSubmit(submittedTasks);
      }
      handleClose();
    },
    [handleSubmit, handleClose],
  );

  const value = useMemo(
    () => ({
      onOpenDialog: handleOpen,
    }),
    [handleOpen],
  );

  return (
    <CreateMultipleTasksDialogContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        {open && (
          <CreateMultipleTasksDialog
            open={open}
            tasks={taskNames}
            date={date}
            context={context}
            onClose={handleClose}
            onCancel={handleDialogCancel}
            onSubmit={handleDialogSubmit}
          />
        )}
      </Suspense>
    </CreateMultipleTasksDialogContext.Provider>
  );
};
